// Poprawiony plik: KalkulatorMaterialowZPlanu.jsx
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
// npm install pdfjs-dist
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url";
// (lub polegaj na CDN)
import defaultPlanUrl from "./plan.png";

const PDF_WORKER_CDN =
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";

// --- Funkcje pomocnicze (poza komponentem) ---
function parseMeters(v) {
  if (typeof v !== "string") v = String(v ?? "");
  v = v.trim().replace(",", ".");
  const n = parseFloat(v);
  return isFinite(n) ? n : 0;
}

function dist(x1, y1, x2, y2) {
  return Math.hypot(x2 - x1, y2 - y1);
}

function mToText(m) {
  return m.toFixed(2) + " m";
}

function pointToSegmentDistance(px, py, x1, y1, x2, y2) {
  const A = px - x1;
  const B = py - y1;
  const C = x2 - x1;
  const D = y2 - y1;
  const lenSq = C * C + D * D;
  let t = lenSq ? (A * C + B * D) / lenSq : 0;
  t = Math.max(0, Math.min(1, t));
  const nx = x1 + t * C;
  const ny = y1 + t * D;
  return Math.hypot(px - nx, py - ny);
}

// --- Główny komponent ---
export default function KalkulatorMaterialowZPlanu() {
  const canvasRef = useRef(null);
  const fileRef = useRef(null);
  const pdfjsLibRef = useRef(null);
  const dprRef = useRef(window.devicePixelRatio || 1);

  // --- Tryb i Opcje ---
  const [mode, setMode] = useState("scale");
  const [snapEnabled, setSnapEnabled] = useState(true);
  const [snapStrict, setSnapStrict] = useState(false);
  const [autoPdfScale, setAutoPdfScale] = useState(true);

  // --- Dane wejściowe ---
  const [scaleKnownM, setScaleKnownM] = useState("5.07");
  const [heightGlobal, setHeightGlobal] = useState(2.7);
  const [heightFromLast, setHeightFromLast] = useState(true);
  const [segHeight, setSegHeight] = useState("");
  const [screedThick, setScreedThick] = useState(6);
  const [screedCoverage, setScreedCoverage] = useState(1.5);

  // --- GŁÓWNY STAN APLIKACJI ---
  const [hint, setHint] = useState("Załaduj obraz lub PDF.");
  const [bgImg, setBgImg] = useState(null);
  const [view, setView] = useState({ x: 0, y: 0, zoom: 1 });
  // Początkowa skala - ustawiona na małą, sensowną domyślną wartość (1 cm / px)
  // Dzięki temu pierwszy pomiar pokaże metry, ale nie będzie przeskalowany jak wcześniej
  const [scaleMPerPx, setScaleMPerPx] = useState(1);

  const [measures, setMeasures] = useState([]);
  const [floors, setFloors] = useState([]);
  const [snapPoints, setSnapPoints] = useState([]);
  const [pdfDims, setPdfDims] = useState([]);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(0);

  // --- Stan tymczasowy (rysowanie) ---
  const [measuring, setMeasuring] = useState(null);
  const [scaleGuide, setScaleGuide] = useState(null);
  const [lastScaleSeg, setLastScaleSeg] = useState(null);
  const [lastScaleMeters, setLastScaleMeters] = useState(0);
  const [floorPoly, setFloorPoly] = useState([]);
  const [hoverSnap, setHoverSnap] = useState(null);
  const [lastLenText, setLastLenText] = useState("—");
  // Structural elements: slup (column), beam (podciag/nadciag/wieniec), strop (slab)
  const [structuralElements, setStructuralElements] = useState([]);
  const [isChoosingPage, setIsChoosingPage] = useState(false);
  const pageSelectInputRef = useRef(null);
  const defaultPlanLoadedRef = useRef(false);

  // Panning state (smooth, rAF-throttled to avoid re-renders during drag)
  const panRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    startViewX: 0,
    startViewY: 0,
    rafPending: false,
  });
  const currentViewRef = useRef(view);

  // keep currentViewRef in sync with state
  useEffect(() => {
    currentViewRef.current = view;
  }, [view]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const onMouseDown = (e) => {
      if (e.button !== 2) return;
      e.preventDefault();
      panRef.current.active = true;
      panRef.current.startX = e.clientX;
      panRef.current.startY = e.clientY;
      panRef.current.startViewX = currentViewRef.current.x;
      panRef.current.startViewY = currentViewRef.current.y;
    };

    const onMouseMove = (e) => {
      if (!panRef.current.active) return;
      const dx =
        (e.clientX - panRef.current.startX) / currentViewRef.current.zoom;
      const dy =
        (e.clientY - panRef.current.startY) / currentViewRef.current.zoom;

      let newX = panRef.current.startViewX + dx;
      let newY = panRef.current.startViewY + dy;

      // Clamping - margin
      if (bgImg) {
        const margin = 100;
        const dpr = dprRef.current;
        const canvasW = canvas.width / dpr;
        const canvasH = canvas.height / dpr;

        const minX =
          -(bgImg.width * currentViewRef.current.zoom + margin) /
          currentViewRef.current.zoom;
        const maxX = (canvasW + margin) / currentViewRef.current.zoom;
        const minY =
          -(bgImg.height * currentViewRef.current.zoom + margin) /
          currentViewRef.current.zoom;
        const maxY = (canvasH + margin) / currentViewRef.current.zoom;

        newX = Math.max(Math.min(newX, maxX), minX);
        newY = Math.max(Math.min(newY, maxY), minY);
      }

      // Throttle updates with rAF to avoid many re-renders
      currentViewRef.current = {
        x: newX,
        y: newY,
        zoom: currentViewRef.current.zoom,
      };
      if (!panRef.current.rafPending) {
        panRef.current.rafPending = true;
        requestAnimationFrame(() => {
          panRef.current.rafPending = false;
          setView((prev) => ({
            ...prev,
            x: currentViewRef.current.x,
            y: currentViewRef.current.y,
          }));
        });
      }
    };

    const onMouseUp = () => {
      panRef.current.active = false;
    };

    const onContext = (e) => {
      if (panRef.current.active) e.preventDefault();
    };

    canvas.addEventListener("mousedown", onMouseDown);
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    canvas.addEventListener("contextmenu", onContext);

    return () => {
      canvas.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      canvas.removeEventListener("contextmenu", onContext);
    };
  }, [bgImg]);

  // Initialize PDF.js
  useEffect(() => {
    (async () => {
      try {
        const pdfjsLib = await import("pdfjs-dist/legacy/build/pdf");
        const pdfWorkerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

        pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerSrc;
        pdfjsLibRef.current = pdfjsLib;
      } catch (e) {
        console.error("Błąd inicjalizacji PDF.js:", e);
        alert("Nie udało się zainicjalizować PDF.js");
      }
    })();
  }, []);

  // --- Konwersje i Obliczenia ---
  const pxToM = useCallback(
    (px) => (scaleMPerPx ? px * scaleMPerPx : 0),
    [scaleMPerPx]
  );
  const segLenMeters = useCallback(
    (seg) => pxToM(dist(seg.x1, seg.y1, seg.x2, seg.y2)),
    [pxToM]
  );
  const screenToWorld = useCallback(
    (x, y) => ({
      x: x / view.zoom - view.x,
      y: y / view.zoom - view.y,
    }),
    [view]
  );

  const polyAreaMeters = useCallback(
    (poly) => {
      if (poly.length < 3 || !scaleMPerPx) return 0;
      let s = 0;
      for (let i = 0; i < poly.length; i++) {
        const [x1, y1] = poly[i];
        const [x2, y2] = poly[(i + 1) % poly.length];
        s += x1 * y2 - x2 * y1;
      }
      const areaPx = Math.abs(s) / 2;
      return areaPx * scaleMPerPx * scaleMPerPx;
    },
    [scaleMPerPx]
  );

  // --- Obliczenia Wyjściowe (useMemo) ---

  // POPRAWKA: Nowy, czytelny feedback skali
  const scaleFeedback = useMemo(() => {
    if (!scaleMPerPx) return { mToPx: "—", pxToM: "—" };
    const pxPerMeter = 1 / scaleMPerPx;
    const mPer100Px = scaleMPerPx * 100;
    return {
      mToPx: `1 m = ${pxPerMeter.toFixed(1)} px`,
      pxToM: `100 px = ${mPer100Px.toFixed(2)} m`,
    };
  }, [scaleMPerPx]);

  // POPRAWKA: Krytyczny błąd w 'reduce'. Powinno być (sum, it) => sum + it.m
  const sumLen = useMemo(
    () =>
      measures
        .filter((it) => it.type !== "grubosc")
        .reduce((sum, it) => sum + (it.m || 0), 0),
    [measures]
  );

  const sumLenText = useMemo(() => mToText(sumLen), [sumLen]);

  const wallArea = useMemo(() => {
    const hGlob = Number(heightGlobal) || 0;
    return measures
      .filter((it) => it.type !== "grubosc")
      .reduce((s, it) => {
        const hUse = typeof it.h === "number" && it.h > 0 ? it.h : hGlob;
        const len = it.m || 0;
        return s + (hUse > 0 ? len * hUse : 0);
      }, 0);
  }, [measures, heightGlobal]);

  const wallAreaText = useMemo(() => `${wallArea.toFixed(2)} m²`, [wallArea]);
  const floorArea = useMemo(
    () => floors.reduce((s, poly) => s + polyAreaMeters(poly), 0), // POPRAWKA
    [floors, polyAreaMeters]
  );

  const floorVol = useMemo(
    () => (floorArea * (Number(screedThick) || 0)) / 100,
    [floorArea, screedThick]
  );

  const floorBags = useMemo(() => {
    const coverage = Number(screedCoverage) || 1;
    const thick = Number(screedThick) || 0;
    return coverage > 0 ? Math.ceil((floorArea * thick) / coverage) : 0;
  }, [floorArea, screedThick, screedCoverage]);

  const floorAreaText = useMemo(
    () => `${floorArea.toFixed(2)} m²`,
    [floorArea]
  );
  const floorVolText = useMemo(() => `${floorVol.toFixed(3)} m³`, [floorVol]);
  const floorBagsText = useMemo(() => String(floorBags), [floorBags]);

  const sumHeight = useMemo(() => {
    const hGlob = Number(heightGlobal) || 0;
    return measures
      .filter((it) => it.type !== "grubosc")
      .reduce((s, it) => {
        const hUse = typeof it.h === "number" && it.h > 0 ? it.h : hGlob;
        return s + (hUse > 0 ? hUse : 0);
      }, 0);
  }, [measures, heightGlobal]);

  const sumHeightText = useMemo(() => mToText(sumHeight), [sumHeight]);

  const structuralVolumes = useMemo(() => {
    let volColumns = 0;
    let volBeams = 0;
    let volSlabs = 0;
    for (const el of structuralElements) {
      if (typeof el.volume !== "number") continue;
      if (el.kind === "slup") volColumns += el.volume;
      else if (el.kind === "strop") volSlabs += el.volume;
      else volBeams += el.volume;
    }
    const volTotal = volColumns + volBeams + volSlabs;
    return { volColumns, volBeams, volSlabs, volTotal };
  }, [structuralElements]);

  const structuralTotalVolumeText = useMemo(
    () => `${structuralVolumes.volTotal.toFixed(3)} m³`,
    [structuralVolumes]
  );

  const structuralAreasTotal = useMemo(() => {
    let area = 0;
    for (const el of structuralElements) {
      if (typeof el.area === "number") area += el.area;
    }
    return area;
  }, [structuralElements]);

  const structuralTotalAreaText = useMemo(
    () => `${structuralAreasTotal.toFixed(2)} m²`,
    [structuralAreasTotal]
  );

  const structuralLengths = useMemo(() => {
    let len = 0;
    for (const el of structuralElements) {
      if (typeof el.length === "number") len += el.length;
    }
    return len;
  }, [structuralElements]);

  const structuralTotalLenText = useMemo(
    () => mToText(structuralLengths),
    [structuralLengths]
  );

  // --- Logika Pomiarów ---
  // Store measurements with raw pixels (mPx) and computed meters (m).
  // This prevents first-measure being saved as 0 if scale isn't set yet.
  const addMeasurement = useCallback(
    (segment, pxLen, type) => {
      const hLocal = Number(segHeight) || null;
      const meters = scaleMPerPx ? pxLen * scaleMPerPx : null;
      setMeasures((prev) => [
        ...prev,
        { seg: segment, mPx: pxLen, m: meters, type, h: hLocal },
      ]);
      if (heightFromLast && meters) setHeightGlobal(meters);
    },
    [segHeight, heightFromLast, scaleMPerPx]
  );

  // Add a structural element
  const addStructuralElement = useCallback((el) => {
    // el: { kind, x,y } for column OR { kind, seg } for beam OR { kind, poly } for strop
    setStructuralElements((prev) => [...prev, { id: Date.now(), ...el }]);
  }, []);

  // --- Logika Skali ---
  const nearestDimValueForSegment = useCallback(
    (seg) => {
      const thr = 40;
      let best = null,
        bestD = Infinity;
      for (const t of pdfDims) {
        const d = pointToSegmentDistance(
          t.x,
          t.y,
          seg.x1,
          seg.y1,
          seg.x2,
          seg.y2
        );
        if (d < thr && d < bestD) {
          best = t;
          bestD = d;
        }
      }
      return best ? best.value : null;
    },
    [pdfDims]
  );

  const calibrateScaleFromSegment = useCallback(
    (seg) => {
      const px = dist(seg.x1, seg.y1, seg.x2, seg.y2);
      if (px <= 0) return false;
      const metersKnown = parseMeters(scaleKnownM) || 0;
      if (metersKnown > 0) {
        setScaleMPerPx(metersKnown / px);
        setLastScaleMeters(metersKnown);
        return true;
      }
      if (autoPdfScale && pdfDims.length > 0) {
        const val = nearestDimValueForSegment(seg);
        if (val && isFinite(val)) {
          setScaleMPerPx(val / px);
          setLastScaleMeters(val);
          return true;
        }
      }
      return false;
    },
    [scaleKnownM, autoPdfScale, pdfDims, nearestDimValueForSegment]
  );

  const askScaleInputM = useCallback(
    (seg) => {
      const px = dist(seg.x1, seg.y1, seg.x2, seg.y2);
      const metersKnown = parseMeters(scaleKnownM) || 0;
      if (metersKnown > 0 && px > 0) {
        setScaleMPerPx(metersKnown / px);
        setLastScaleMeters(metersKnown);
        return true;
      }
      const meters = Number(
        prompt("Podaj rzeczywistą długość w metrach:", "1.00")
      );
      if (meters > 0 && px > 0) {
        setScaleMPerPx(meters / px);
        setLastScaleMeters(meters);
        setScaleKnownM(String(meters));
        return true;
      }
      return false;
    },
    [scaleKnownM]
  );

  // --- Logika Snappingu ---
  const getSnappedPoint = useCallback(
    (x, y) => {
      if (!snapEnabled || !snapPoints.length) return null;
      const radiusScreen = 12;
      const radiusWorld = radiusScreen / view.zoom;
      let best = null,
        bestD = Infinity;
      for (const p of snapPoints) {
        const d = Math.hypot(p.x - x, p.y - y);
        if (d < radiusWorld && d < bestD) {
          best = p;
          bestD = d;
        }
      }
      return best;
    },
    [snapEnabled, snapPoints, view.zoom]
  );

  // --- RYSOWANIE (useCallback) ---
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = dprRef.current;
    const W = canvas.width / dpr,
      H = canvas.height / dpr;

    ctx.clearRect(0, 0, W, H);
    ctx.save();
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, W, H);
    ctx.translate(view.x * view.zoom, view.y * view.zoom);
    ctx.scale(view.zoom, view.zoom);

    if (bgImg) {
      ctx.globalAlpha = 0.95;
      ctx.drawImage(bgImg, 0, 0);
      ctx.globalAlpha = 1;
    }

    // --- Funkcje pomocnicze rysowania ---
    function drawLabel(text, x, y, color) {
      ctx.save();
      const lines = String(text).split("\n");
      ctx.font = `bold ${13 / view.zoom}px system-ui, sans-serif`;
      const pad = 6 / view.zoom;
      const lh = 16 / view.zoom;
      const wtxt =
        Math.max(...lines.map((s) => ctx.measureText(s).width)) + pad * 2;
      const htxt = lh * lines.length + pad * 2;
      ctx.translate(x, y);
      ctx.fillStyle = "rgba(255,255,255,0.95)";
      ctx.strokeStyle = color;
      ctx.lineWidth = 1 / view.zoom;
      ctx.shadowColor = "rgba(0,0,0,0.2)";
      ctx.shadowBlur = 4 / view.zoom;
      ctx.fillRect(-wtxt / 2, -htxt / 2, wtxt, htxt);
      ctx.strokeRect(-wtxt / 2, -htxt / 2, wtxt, htxt);
      ctx.shadowColor = "transparent";
      ctx.fillStyle = "#111827";
      ctx.textAlign = "center";
      lines.forEach((ln, i) =>
        ctx.fillText(ln, 0, -htxt / 2 + pad + lh * (i + 0.75))
      );
      ctx.restore();
    }

    function drawMeasure(m, color, labelOverride) {
      if (!m) return;
      const pxLen = dist(m.x1, m.y1, m.x2, m.y2);
      const mCalc = pxToM(pxLen);
      let txt = labelOverride;
      if (!txt) {
        // Jeśli skala jest ustawiona, zawsze pokazuj metry (nawet podczas rysowania)
        if (scaleMPerPx) {
          txt = mToText(mCalc) + "\n" + (mCalc * 100).toFixed(0) + " cm";
        } else {
          // Bez skali, pokazuj pixele
          txt = pxLen.toFixed(0) + " px";
        }
        const v = nearestDimValueForSegment(m);
        if (v && scaleMPerPx) txt += "\nrys: " + v.toFixed(2) + " m";
      }
      ctx.save();
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.setLineDash([]);
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 7 / view.zoom;
      ctx.beginPath();
      ctx.moveTo(m.x1, m.y1);
      ctx.lineTo(m.x2, m.y2);
      ctx.stroke();
      ctx.strokeStyle = color;
      ctx.lineWidth = 4 / view.zoom;
      ctx.beginPath();
      ctx.moveTo(m.x1, m.y1);
      ctx.lineTo(m.x2, m.y2);
      ctx.stroke();
      const r = 5 / view.zoom;
      ctx.fillStyle = "#ffffff";
      ctx.strokeStyle = color;
      ctx.lineWidth = 2 / view.zoom;
      ctx.beginPath();
      ctx.arc(m.x1, m.y1, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(m.x2, m.y2, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      const midx = (m.x1 + m.x2) / 2,
        midy = (m.y1 + m.y2) / 2;
      drawLabel(txt, midx, midy, color);
      ctx.restore();
    }

    // POPRAWKA: Czytelniejsza linijka skali
    function drawScaleAB(seg, meters) {
      const pxLen = dist(seg.x1, seg.y1, seg.x2, seg.y2);
      if (pxLen <= 0 || !scaleMPerPx || meters <= 0) return;
      const ux = (seg.x2 - seg.x1) / pxLen;
      const uy = (seg.y2 - seg.y1) / pxLen;
      const nx = -uy,
        ny = ux; // Wektor normalny (prostopadły)
      const off = 12 / view.zoom; // Odległość od linii
      const tickStepM = meters >= 3 ? 1 : meters >= 1.5 ? 0.5 : 0.25;
      const tickCount = Math.floor(meters / tickStepM + 0.0001);

      ctx.save();
      ctx.lineWidth = 3 / view.zoom; // Grubsza linia
      ctx.strokeStyle = "#ea580c"; // Ciemny pomarańcz
      ctx.fillStyle = "#ea580c";

      // Główna linia linijki
      ctx.beginPath();
      ctx.moveTo(seg.x1 + nx * off, seg.y1 + ny * off);
      ctx.lineTo(seg.x2 + nx * off, seg.y2 + ny * off);
      ctx.stroke();

      // Podziałki (ticks)
      for (let i = 0; i <= tickCount; i++) {
        const m = i * tickStepM;
        const t = m / meters; // Procent długości
        const x = seg.x1 + ux * pxLen * t;
        const y = seg.y1 + uy * pxLen * t;
        // Główne podziałki (co 1m) są dłuższe
        const isMainTick = Math.abs(m - Math.round(m)) < 1e-6;
        const len = (isMainTick ? 10 : 6) / view.zoom;
        ctx.beginPath();
        ctx.moveTo(x + nx * off, y + ny * off);
        ctx.lineTo(x + nx * (off + len), y + ny * (off + len));
        ctx.stroke();

        // Etykiety dla głównych podziałek
        if (isMainTick) {
          ctx.save();
          ctx.font = `bold ${12 / view.zoom}px system-ui, sans-serif`;
          ctx.textAlign = "center";
          ctx.fillText(
            `${Math.round(m)} m`,
            x + nx * (off + len + 8 / view.zoom),
            y + ny * (off + len + 8 / view.zoom)
          );
          ctx.restore();
        }
      }
      ctx.restore();
    }

    // --- Główna logika rysowania ---
    ctx.fillStyle = "rgba(249,115,22,0.10)";
    ctx.strokeStyle = "#f97316";
    ctx.lineWidth = 2 / view.zoom;
    const pointRadius = 10 / view.zoom;

    for (const poly of floors) {
      if (poly.length < 2) continue;
      ctx.beginPath();
      ctx.moveTo(poly[0][0], poly[0][1]);
      for (let i = 1; i < poly.length; i++) ctx.lineTo(poly[i][0], poly[i][1]);
      if (poly.length > 2) ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = "#f97316";
      for (const p of poly) {
        ctx.beginPath();
        ctx.arc(p[0], p[1], pointRadius, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Draw structural elements
    for (const el of structuralElements) {
      if (!el || !el.kind) continue;
      if (el.kind === "slup") {
        // draw column as filled square centered at x,y with base size (w,d)
        const bw = (el.w || 0.3) / scaleMPerPx || 30 / view.zoom; // px if scale missing
        const bh = (el.d || 0.3) / scaleMPerPx || 30 / view.zoom;
        const cx = el.x || 0;
        const cy = el.y || 0;
        ctx.save();
        ctx.fillStyle = "rgba(99,102,241,0.6)"; // indigo
        ctx.strokeStyle = "#4f46e5";
        ctx.lineWidth = 2 / view.zoom;
        ctx.beginPath();
        ctx.rect(cx - bw / 2, cy - bh / 2, bw, bh);
        ctx.fill();
        ctx.stroke();
        ctx.restore();
        // label volume + area podstawy
        if (typeof el.volume === "number") {
          let label = `${el.volume.toFixed(3)} m³`;
          if (typeof el.area === "number") {
            label += `\n${el.area.toFixed(2)} m²`;
          }
          drawLabel(label, cx, cy - bh / 2 - 12 / view.zoom, "#4f46e5");
        }
      } else if (
        el.kind === "beam" ||
        el.kind === "podciag" ||
        el.kind === "nadciag" ||
        el.kind === "wieniec"
      ) {
        const seg = el.seg;
        if (!seg) continue;
        const color =
          el.kind === "podciag"
            ? "#1d4ed8" // niebieski
            : el.kind === "nadciag"
            ? "#16a34a" // zielony
            : el.kind === "wieniec"
            ? "#f97316" // pomarańczowy
            : "#9d174d"; // domyślny (beam)
        ctx.save();
        ctx.strokeStyle = color;
        ctx.lineWidth = (el.sectionWidthPx || 6) / view.zoom;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(seg.x1, seg.y1);
        ctx.lineTo(seg.x2, seg.y2);
        ctx.stroke();
        ctx.restore();
        const midx = (seg.x1 + seg.x2) / 2;
        const midy = (seg.y1 + seg.y2) / 2;
        if (typeof el.volume === "number") {
          let label = `${el.volume.toFixed(3)} m³`;
          if (typeof el.area === "number") {
            label += `\n${el.area.toFixed(2)} m²`;
          }
          drawLabel(label, midx, midy - 10 / view.zoom, color);
        }
      } else if (el.kind === "strop") {
        const poly = el.poly || [];
        if (poly.length < 3) continue;
        ctx.save();
        ctx.fillStyle = "rgba(34,197,94,0.12)";
        ctx.strokeStyle = "#10b981";
        ctx.lineWidth = 2 / view.zoom;
        ctx.beginPath();
        ctx.moveTo(poly[0][0], poly[0][1]);
        for (let i = 1; i < poly.length; i++)
          ctx.lineTo(poly[i][0], poly[i][1]);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        ctx.restore();
        const cx = poly.reduce((s, p) => s + p[0], 0) / poly.length;
        const cy = poly.reduce((s, p) => s + p[1], 0) / poly.length;
        if (typeof el.volume === "number") {
          let label = `${el.volume.toFixed(3)} m³`;
          if (typeof el.area === "number") {
            label += `\n${el.area.toFixed(2)} m²`;
          }
          drawLabel(label, cx, cy - 12 / view.zoom, "#10b981");
        }
      }
    }

    for (const m of measures) {
      const color = m.type === "grubosc" ? "#0284c7" : "#16a34a";
      let label;
      if (typeof m.m === "number") {
        label = `${m.m.toFixed(2)} m\n(${(m.m * 100).toFixed(0)} cm)`;
      } else if (m.mPx != null) {
        label = `${m.mPx.toFixed(0)} px`;
      } else {
        label = "—";
      }
      drawMeasure(m.seg, color, label);
    }

    if (lastScaleSeg) {
      const label = `Skala: ${(
        lastScaleMeters ||
        parseMeters(scaleKnownM) ||
        0
      ).toFixed(2)} m`;
      drawMeasure(lastScaleSeg, "#ea580c", label);
      if (scaleMPerPx > 0) {
        drawScaleAB(
          lastScaleSeg,
          lastScaleMeters || parseMeters(scaleKnownM) || 0
        );
      }
    }

    if (floorPoly.length) {
      ctx.strokeStyle = "#f59e0b";
      ctx.setLineDash([6 / view.zoom, 6 / view.zoom]);
      ctx.beginPath();
      ctx.moveTo(floorPoly[0][0], floorPoly[0][1]);
      for (let i = 1; i < floorPoly.length; i++)
        ctx.lineTo(floorPoly[i][0], floorPoly[i][1]);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = "#f59e0b";
      for (const p of floorPoly) {
        ctx.beginPath();
        ctx.arc(p[0], p[1], pointRadius, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    drawMeasure(measuring, "#f59e0b");
    drawMeasure(scaleGuide, "#fcd34d");

    if (snapEnabled && snapPoints.length) {
      ctx.save();
      ctx.fillStyle = "rgba(249,115,22,0.8)";
      const screenSize = 5 / view.zoom;
      for (const p of snapPoints)
        ctx.fillRect(
          p.x - screenSize / 2,
          p.y - screenSize / 2,
          screenSize,
          screenSize
        );

      if (hoverSnap) {
        ctx.strokeStyle = "#f97316";
        ctx.lineWidth = 2 / view.zoom;
        ctx.beginPath();
        ctx.arc(hoverSnap.x, hoverSnap.y, 6 / view.zoom, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.restore();
    }

    ctx.restore();
    setHint(
      bgImg
        ? `Tryb: ${mode.toUpperCase()} · Rolka: zoom · PPM: przesuwanie · ESC: anuluj`
        : "Załaduj obraz / PDF."
    );
  }, [
    bgImg,
    view,
    mode,
    floors,
    floorPoly,
    measures,
    measuring,
    scaleGuide,
    lastScaleSeg,
    lastScaleMeters,
    scaleKnownM,
    scaleMPerPx,
    snapEnabled,
    snapPoints,
    hoverSnap,
    pxToM,
    nearestDimValueForSegment,
  ]);

  // --- Efekt: Rysuj ponownie gdy zmieni się stan ---
  useEffect(() => {
    draw();
  }, [draw]);

  // ... (Efekt: Aktualizuj tekst "Ostatni pomiar" - bez zmian) ...
  useEffect(() => {
    if (measuring) {
      const pxLen = dist(
        measuring.x1,
        measuring.y1,
        measuring.x2,
        measuring.y2
      );
      const mCalc = pxToM(pxLen);
      let text = scaleMPerPx ? mToText(mCalc) : pxLen.toFixed(0) + " px";
      const v = nearestDimValueForSegment(measuring);
      if (v) {
        text += scaleMPerPx
          ? ` • rys: ${v.toFixed(2)} m`
          : ` • rys: ${v.toFixed(2)} m (auto-skala)`;
      }
      setLastLenText(text);
    } else {
      setLastLenText("—");
    }
  }, [measuring, scaleMPerPx, pxToM, nearestDimValueForSegment]);

  // ... (Efekt: Obsługa zmiany rozmiaru okna - bez zmian) ...
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    function fitCanvas() {
      const rect = canvas.getBoundingClientRect();
      const w = Math.max(200, Math.floor(rect.width));
      const h = Math.max(200, Math.floor(rect.height));
      const dpr = dprRef.current;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      draw();
    }

    const resizeObserver = new ResizeObserver(fitCanvas);
    resizeObserver.observe(canvas.parentElement || canvas);
    fitCanvas();

    return () => resizeObserver.disconnect();
  }, [draw]);

  // ... (Efekt: Główne Listenery - bez zmian) ...
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let panning = false,
      panStart = { x: 0, y: 0 },
      viewStart = { x: 0, y: 0 };

    function handlePrimaryClick(worldX, worldY) {
      let wx = worldX,
        wy = worldY;
      const sp = getSnappedPoint(wx, wy);
      if (sp) {
        wx = sp.x;
        wy = sp.y;
      }

      if (snapStrict && snapEnabled && !sp) return;

      if (mode === "scale") {
        if (!scaleGuide) {
          setScaleGuide({ x1: wx, y1: wy, x2: wx, y2: wy });
        } else {
          const finalGuide = { ...scaleGuide, x2: wx, y2: wy };
          calibrateScaleFromSegment(finalGuide) || askScaleInputM(finalGuide);
          setLastScaleSeg(finalGuide);
          setScaleGuide(null);
        }
      } else if (mode === "measure" || mode === "thick") {
        if (!measuring) {
          setMeasuring({ x1: wx, y1: wy, x2: wx, y2: wy });
        } else {
          const finalMeasure = { ...measuring, x2: wx, y2: wy };
          if (!scaleMPerPx && autoPdfScale) {
            calibrateScaleFromSegment(finalMeasure);
          }
          const pxLen = dist(
            finalMeasure.x1,
            finalMeasure.y1,
            finalMeasure.x2,
            finalMeasure.y2
          );
          addMeasurement(
            finalMeasure,
            pxLen,
            mode === "thick" ? "grubosc" : "odcinek"
          );
          setMeasuring(null);
        }
      } else if (mode === "floor" || mode === "strop") {
        setFloorPoly((prev) => [...prev, [wx, wy]]);
      } else if (mode === "column") {
        // place a column (słup) at clicked point
        const w =
          parseFloat(prompt("Szerokość podstawy [m] (np. 0.3)", "0.3")) || 0.3;
        const d =
          parseFloat(prompt("Głębokość podstawy [m] (np. 0.3)", "0.3")) || 0.3;
        const h =
          parseFloat(prompt("Wysokość słupa [m] (np. 3.0)", "3.0")) || 3.0;
        const baseArea = w * d;
        const vol = baseArea * h;
        addStructuralElement({
          kind: "slup",
          x: wx,
          y: wy,
          w,
          d,
          h,
          area: baseArea,
          volume: vol,
        });
      } else if (
        mode === "podciag" ||
        mode === "nadciag" ||
        mode === "wieniec"
      ) {
        // beams: behave like measuring segments
        if (!measuring) {
          setMeasuring({ x1: wx, y1: wy, x2: wx, y2: wy });
        } else {
          const finalSeg = { ...measuring, x2: wx, y2: wy };
          const pxLen = dist(
            finalSeg.x1,
            finalSeg.y1,
            finalSeg.x2,
            finalSeg.y2
          );
          const lenM = pxToM(pxLen);
          const bw =
            parseFloat(prompt("Szerokość przekroju [m] (np. 0.3)", "0.3")) ||
            0.3;
          const bd =
            parseFloat(prompt("Wysokość przekroju [m] (np. 0.5)", "0.5")) ||
            0.5;
          const sectionArea = bw * bd;
          const vol = lenM * sectionArea;
          const surfaceArea = lenM * bw; // powierzchnia (długość × szerokość)
          addStructuralElement({
            kind: mode,
            seg: finalSeg,
            sectionW: bw,
            sectionH: bd,
            length: lenM,
            area: surfaceArea,
            volume: vol,
          });
          setMeasuring(null);
        }
      }
    }

    const onWheel = (e) => {
      e.preventDefault();
      const delta = -Math.sign(e.deltaY) * 0.1;
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left,
        my = e.clientY - rect.top;

      setView((prevView) => {
        const before = {
          x: mx / prevView.zoom - prevView.x,
          y: my / prevView.zoom - prevView.y,
        };
        const newZoom = Math.max(
          0.1,
          Math.min(10, prevView.zoom * (1 + delta))
        );
        const newX = mx / newZoom - before.x;
        const newY = my / newZoom - before.y;
        return { x: newX, y: newY, zoom: newZoom };
      });
    };

    const onMouseDown = (e) => {
      if (e.button === 2) {
        panning = true;
        panStart = { x: e.clientX, y: e.clientY };
        viewStart = { ...view };
        e.preventDefault();
        return;
      }
      const rect = canvas.getBoundingClientRect();
      const p = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
      handlePrimaryClick(p.x, p.y);
    };

    const onMouseMove = (e) => {
      if (panning) {
        const dx = (e.clientX - panStart.x) / viewStart.zoom;
        const dy = (e.clientY - panStart.y) / viewStart.zoom;
        setView({
          x: viewStart.x + dx,
          y: viewStart.y + dy,
          zoom: viewStart.zoom,
        });
        return;
      }

      const rect = canvas.getBoundingClientRect();
      let { x, y } = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
      const sp = getSnappedPoint(x, y);
      if (sp) {
        x = sp.x;
        y = sp.y;
        setHoverSnap(sp);
      } else setHoverSnap(null);

      if (mode === "scale" && scaleGuide) {
        setScaleGuide((prev) => ({ ...prev, x2: x, y2: y }));
      }
      if (
        (mode === "measure" ||
          mode === "thick" ||
          mode === "podciag" ||
          mode === "nadciag" ||
          mode === "wieniec") &&
        measuring
      ) {
        setMeasuring((prev) => ({ ...prev, x2: x, y2: y }));

        // Auto-pan: Jeśli punkt wyciągnięcia jest blisko krawędzi ekranu, przesuwaj widok
        const marginPx = 80; // Pixel margin od krawędzi
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        const W = rect.width;
        const H = rect.height;

        let panDx = 0,
          panDy = 0;
        // Jeśli mysz jest blisko LEWEJ krawędzi, przesuń widok W PRAWO (aby zobaczyć zawartość poza lewą krawędzią)
        if (mouseX < marginPx) panDx = 20;
        // Jeśli mysz jest blisko PRAWEJ krawędzi, przesuń widok W LEWO
        if (mouseX > W - marginPx) panDx = -20;
        // Jeśli mysz jest blisko GÓRNEJ krawędzi, przesuń widok W DÓŁ
        if (mouseY < marginPx) panDy = 20;
        // Jeśli mysz jest blisko DOLNEJ krawędzi, przesuń widok W GÓRĘ
        if (mouseY > H - marginPx) panDy = -20;

        if (panDx !== 0 || panDy !== 0) {
          setView((prevView) => ({
            ...prevView,
            x: prevView.x + panDx / prevView.zoom,
            y: prevView.y + panDy / prevView.zoom,
          }));
        }
      }
    };

    const onMouseUp = () => {
      panning = false;
    };

    const onContextMenu = (e) => e.preventDefault();

    const onKeyDown = (e) => {
      if (e.key === "Enter" && mode === "scale" && scaleGuide) {
        calibrateScaleFromSegment(scaleGuide) || askScaleInputM(scaleGuide);
        setLastScaleSeg({ ...scaleGuide });
        setScaleGuide(null);
      }
      if (e.key === "Escape") {
        setMeasuring(null);
        setScaleGuide(null);
        setFloorPoly([]);
      }
    };

    canvas.addEventListener("wheel", onWheel, { passive: false });
    canvas.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    canvas.addEventListener("contextmenu", onContextMenu);
    window.addEventListener("keydown", onKeyDown);

    return () => {
      canvas.removeEventListener("wheel", onWheel);
      canvas.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      canvas.removeEventListener("contextmenu", onContextMenu);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [
    mode,
    snapEnabled,
    snapStrict,
    autoPdfScale,
    scaleMPerPx,
    getSnappedPoint,
    screenToWorld,
    addMeasurement,
    segLenMeters,
    calibrateScaleFromSegment,
    askScaleInputM,
    scaleGuide,
    measuring,
    view,
  ]);

  // --- Handlery UI ---

  const centerImage = useCallback((img) => {
    const canvas = canvasRef.current;
    if (!img || !canvas) return;
    const dpr = dprRef.current;
    const W = canvas.width / dpr,
      H = canvas.height / dpr;
    const z = Math.min((W * 0.9) / img.width, (H * 0.9) / img.height);
    setView({
      zoom: z,
      x: (W / z - img.width) / 2,
      y: (H / z - img.height) / 2,
    });
  }, []);

  // Load bundled plan.png as the default background so the user always sees a base plan.
  useEffect(() => {
    if (defaultPlanLoadedRef.current || !defaultPlanUrl) return;
    const img = new Image();
    img.onload = () => {
      defaultPlanLoadedRef.current = true;
      setBgImg(img);
      centerImage(img);
    };
    img.src = defaultPlanUrl;
  }, [centerImage]);

  const renderPage = useCallback(
    async (pageNumber) => {
      if (!pdfDoc) return;
      try {
        const page = await pdfDoc.getPage(pageNumber);
        const viewport = page.getViewport({ scale: 2 });
        const offscreenCanvas = document.createElement("canvas");
        offscreenCanvas.width = viewport.width;
        offscreenCanvas.height = viewport.height;
        const offscreenCtx = offscreenCanvas.getContext("2d");
        await page.render({ canvasContext: offscreenCtx, viewport }).promise;
        const img = new Image();
        img.onload = () => {
          setBgImg(img);
          centerImage(img);
        };
        img.src = offscreenCanvas.toDataURL("image/png");

        const text = await page.getTextContent();
        const dims = [];
        for (const item of text.items) {
          const raw = (item.str || "").trim();
          if (!raw) continue;
          const m = raw.match(/^\d{1,3}([\\.,]\d+)?$/);
          if (!m) continue;
          const val = parseFloat(raw.replace(",", "."));
          const tr = item.transform;
          const x = tr[4] * viewport.scale;
          const y = viewport.height - tr[5] * viewport.scale;
          dims.push({ x, y, value: val });
        }
        setPdfDims(dims);
      } catch (err) {
        console.error("Błąd ładowania strony PDF:", err);
        alert("Nie udało się wczytać strony PDF.");
      }
    },
    [pdfDoc, centerImage]
  );

  useEffect(() => {
    if (pdfDoc) {
      renderPage(pageNum);
    }
  }, [pdfDoc, pageNum, renderPage]);

  // ... (renderPdfFirstPage - bez zmian) ...
  const renderPdfFirstPage = useCallback(
    async (file) => {
      let pdfjs = pdfjsLibRef.current;
      if (!pdfjs) {
        if (window.pdfjsLib) {
          if (window.pdfjsLib.GlobalWorkerOptions)
            window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_WORKER_CDN;
          pdfjsLibRef.current = window.pdfjsLib;
          pdfjs = window.pdfjsLib;
        } else {
          alert(
            "Biblioteka PDF.js nie jest załadowana. Spróbuj odświeżyć stronę."
          );
          return;
        }
      }
      try {
        const arrayBuf = await file.arrayBuffer();
        const pdf = await pdfjs.getDocument({ data: arrayBuf }).promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 2 });
        const offscreenCanvas = document.createElement("canvas");
        offscreenCanvas.width = viewport.width;
        offscreenCanvas.height = viewport.height;
        const offscreenCtx = offscreenCanvas.getContext("2d");
        await page.render({ canvasContext: offscreenCtx, viewport }).promise;
        const img = new Image();
        img.onload = () => {
          setBgImg(img);
          centerImage(img);
        };
        img.src = offscreenCanvas.toDataURL("image/png");
        const text = await page.getTextContent();
        const dims = [];
        for (const item of text.items) {
          const raw = (item.str || "").trim();
          if (!raw) continue;
          const m = raw.match(/^\d{1,3}([\\.,]\d+)?$/);
          if (!m) continue;
          const val = parseFloat(raw.replace(",", "."));
          const tr = item.transform;
          const x = tr[4] * viewport.scale;
          const y = viewport.height - tr[5] * viewport.scale;
          dims.push({ x, y, value: val });
        }
        setPdfDims(dims);
      } catch (err) {
        console.error("Błąd ładowania PDF:", err);
        alert("Nie udało się wczytać PDF.");
      }
    },
    [centerImage]
  );

  const handleFileInputChange = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setPdfDims([]);
      setSnapPoints([]);
      setMeasures([]);
      setFloors([]);
      setFloorPoly([]);
      setMeasuring(null);
      setScaleGuide(null);
      setLastScaleSeg(null);
      // keep existing scale when loading a new plan (user may want to keep it)
      // but clear measures/floors to avoid mismatch
      setScaleMPerPx((s) => s || 0);
      setBgImg(null);
      setPdfDoc(null);
      setNumPages(0);
      setPageNum(1);

      if (
        file.type === "application/pdf" ||
        file.name.toLowerCase().endsWith(".pdf")
      ) {
        let pdfjs = pdfjsLibRef.current;
        if (!pdfjs) {
          // Fallback to window.pdfjsLib if needed
          if (window.pdfjsLib) {
            if (window.pdfjsLib.GlobalWorkerOptions)
              window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_WORKER_CDN;
            pdfjsLibRef.current = window.pdfjsLib;
            pdfjs = window.pdfjsLib;
          } else {
            alert(
              "Biblioteka PDF.js nie jest załadowana. Spróbuj odświeżyć stronę."
            );
            return;
          }
        }
        try {
          const arrayBuf = await file.arrayBuffer();
          const pdf = await pdfjs.getDocument({ data: arrayBuf }).promise;
          setPdfDoc(pdf);
          setNumPages(pdf.numPages);
          setPageNum(1); // Start on page 1
        } catch (err) {
          console.error("Błąd ładowania PDF:", err);
          alert("Nie udało się wczytać PDF.");
        }
      } else {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
          setBgImg(img);
          centerImage(img);
          URL.revokeObjectURL(url);
        };
        img.src = url;
      }
    },
    [centerImage]
  );

  // Render selected PDF page when pdfDoc/pageNum changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!pdfDoc) return;
        const page = await pdfDoc.getPage(pageNum);
        const viewport = page.getViewport({ scale: 2 });
        const offscreenCanvas = document.createElement("canvas");
        offscreenCanvas.width = viewport.width;
        offscreenCanvas.height = viewport.height;
        const offscreenCtx = offscreenCanvas.getContext("2d");
        await page.render({ canvasContext: offscreenCtx, viewport }).promise;
        if (cancelled) return;
        const img = new Image();
        img.onload = () => {
          setBgImg(img);
          centerImage(img);
        };
        img.src = offscreenCanvas.toDataURL("image/png");

        const text = await page.getTextContent();
        const dims = [];
        for (const item of text.items) {
          const raw = (item.str || "").trim();
          if (!raw) continue;
          const m = raw.match(/^\d{1,3}([\.,]\d+)?$/);
          if (!m) continue;
          const val = parseFloat(raw.replace(",", "."));
          const tr = item.transform;
          const x = tr[4] * viewport.scale;
          const y = viewport.height - tr[5] * viewport.scale;
          dims.push({ x, y, value: val });
        }
        if (!cancelled) setPdfDims(dims);
      } catch (err) {
        console.error("Błąd renderowania strony PDF:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pdfDoc, pageNum, centerImage]);

  // ... (analyzePlanForIntersections - bez zmian) ...
  const analyzePlanForIntersections = useCallback(async (img) => {
    if (!img) return;
    setSnapPoints([]);
    const maxDim = 1200;
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    const w = Math.round(img.width * scale),
      h = Math.round(img.height * scale);
    const off = document.createElement("canvas");
    off.width = w;
    off.height = h;
    const oc = off.getContext("2d");
    oc.drawImage(img, 0, 0, w, h);
    const imageData = oc.getImageData(0, 0, w, h);
    const data = imageData.data;
    const gray = new Float32Array(w * h);
    for (let i = 0, j = 0; i < data.length; i += 4, j++)
      gray[j] = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
    const Gx = [-1, 0, 1, -2, 0, 2, -1, 0, 1];
    const Gy = [-1, -2, -1, 0, 0, 0, 1, 2, 1];
    const mag = new Float32Array(w * h);
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        let sx = 0,
          sy = 0,
          k = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            const v = gray[(y + dy) * w + (x + dx)];
            sx += v * Gx[k];
            sy += v * Gy[k];
            k++;
          }
        }
        mag[y * w + x] = Math.hypot(sx, sy);
      }
    }
    let sum = 0;
    for (let i = 0; i < mag.length; i++) sum += mag[i];
    const mean = sum / mag.length;
    const thresh = mean * 1.5;
    const angleStep = (Math.PI / 180) * 2;
    const angles = [];
    for (let a = 0; a < Math.PI; a += angleStep) angles.push(a);
    const cos = angles.map(Math.cos),
      sin = angles.map(Math.sin);
    const diag = Math.hypot(w, h);
    const rhoMax = Math.ceil(diag);
    const acc = new Uint32Array(angles.length * (rhoMax * 2 + 1));
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const v = mag[y * w + x];
        if (v < thresh) continue;
        for (let ai = 0; ai < angles.length; ai++) {
          const rho = Math.round(x * cos[ai] + y * sin[ai]);
          const idx = ai * (rhoMax * 2 + 1) + (rho + rhoMax);
          acc[idx]++;
        }
      }
    }
    const K = 50;
    const lines = [];
    for (let k = 0; k < K; k++) {
      let best = 0,
        bestIdx = -1;
      for (let i = 0; i < acc.length; i++) {
        if (acc[i] > best) {
          best = acc[i];
          bestIdx = i;
        }
      }
      if (best < 10) break;
      acc[bestIdx] = 0;
      const ai = Math.floor(bestIdx / (rhoMax * 2 + 1));
      const ri = (bestIdx % (rhoMax * 2 + 1)) - rhoMax;
      lines.push({ theta: angles[ai], rho: ri });
      const supA = 2,
        supR = 10;
      for (let da = -supA; da <= supA; da++) {
        const aii = ai + da;
        if (aii < 0 || aii >= angles.length) continue;
        for (let dr = -supR; dr <= supR; dr++) {
          const rii = ri + dr;
          const idx = aii * (rhoMax * 2 + 1) + (rii + rhoMax);
          if (idx >= 0 && idx < acc.length) acc[idx] = 0;
        }
      }
    }
    function intersectLines(L1, L2) {
      const a1 = Math.cos(L1.theta),
        b1 = Math.sin(L1.theta),
        a2 = Math.cos(L2.theta),
        b2 = Math.sin(L2.theta);
      const det = a1 * b2 - a2 * b1;
      if (Math.abs(det) < 1e-6) return null;
      const x = (L1.rho * b2 - L2.rho * b1) / det;
      const y = (a1 * L2.rho - a2 * L1.rho) / det;
      return { x, y };
    }
    const pts = [];
    for (let i = 0; i < lines.length; i++)
      for (let j = i + 1; j < lines.length; j++) {
        const L1 = lines[i],
          L2 = lines[j];
        const d = Math.abs(L1.theta - L2.theta);
        if (
          d < (10 * Math.PI) / 180 ||
          Math.abs(d - Math.PI) < (10 * Math.PI) / 180
        )
          continue;
        const p = intersectLines(L1, L2);
        if (!p) continue;
        if (p.x < 0 || p.y < 0 || p.x > w || p.y > h) continue;
        pts.push(p);
      }
    const cell = 6,
      seen = new Set(),
      uniq = [];
    for (const p of pts) {
      const key = Math.round(p.x / cell) + ":" + Math.round(p.y / cell);
      if (seen.has(key)) continue;
      seen.add(key);
      // map back to original image coordinates
      uniq.push({
        x: Number((p.x / scale).toFixed(2)),
        y: Number((p.y / scale).toFixed(2)),
      });
    }
    setSnapPoints(uniq);
  }, []);

  function handleAnalyzeClick() {
    if (!bgImg) {
      alert("Najpierw wczytaj plan.");
      return;
    }
    analyzePlanForIntersections(bgImg);
  }

  function handleClear() {
    setMeasuring(null);
    setScaleGuide(null);
    setLastScaleSeg(null);
    setMeasures([]);
    setFloorPoly([]);
    setFloors([]);
    setStructuralElements([]);
    setSnapPoints([]);
  }

  function handleUndo() {
    if ((mode === "floor" || mode === "strop") && floorPoly.length > 0) {
      setFloorPoly((prev) => prev.slice(0, -1));
    } else if (
      (mode === "column" ||
        mode === "podciag" ||
        mode === "nadciag" ||
        mode === "wieniec" ||
        mode === "floor" ||
        mode === "strop") &&
      structuralElements.length > 0
    ) {
      setStructuralElements((prev) => prev.slice(0, -1));
    } else if (measures.length > 0) {
      setMeasures((prev) => prev.slice(0, -1));
    } else if (floors.length > 0) {
      setFloors((prev) => prev.slice(0, -1));
    }
  }

  function handleExportCsv() {
    const rows = [
      ["#", "typ", "długość [m]", "wysokość [m]", "powierzchnia [m²]"],
    ];
    const hGlob = Number(heightGlobal) || 0;
    measures.forEach((it, i) => {
      const hUse = typeof it.h === "number" && it.h > 0 ? it.h : hGlob;
      const area = it.type !== "grubosc" && hUse > 0 ? it.m * hUse : 0;
      rows.push([
        i + 1,
        it.type,
        it.m.toFixed(3),
        hUse ? hUse.toFixed(3) : "",
        area ? area.toFixed(3) : "",
      ]);
    });
    const csv = rows.map((r) => r.join(";")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "pomiary.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  }

  function handleCalcFloor() {
    if (floorPoly.length < 3) {
      alert("Narysuj obrys (min. 3 punkty) w trybie Posadzka lub Strop.");
      return;
    }
    setFloors((prev) => [...prev, floorPoly]);
    setFloorPoly([]);
  }

  function handleCalcStrop() {
    if (floorPoly.length < 3) {
      alert("Narysuj obrys (min. 3 punkty) w trybie Posadzka lub Strop.");
      return;
    }
    const area = polyAreaMeters(floorPoly);
    const thick =
      parseFloat(prompt("Grubość stropu [cm]", String(screedThick))) ||
      screedThick;
    const vol = (area * (Number(thick) || 0)) / 100;
    // Save as structural element
    addStructuralElement({
      kind: "strop",
      poly: floorPoly,
      thickness: thick,
      area,
      volume: vol,
    });
    setFloorPoly([]);
  }

  // ... (Efekt do aktualizacji skali - bez zmian) ...
  useEffect(() => {
    if (lastScaleSeg) {
      const metersKnown = parseMeters(scaleKnownM) || 0;
      const px = dist(
        lastScaleSeg.x1,
        lastScaleSeg.y1,
        lastScaleSeg.x2,
        lastScaleSeg.y2
      );
      if (metersKnown > 0 && px > 0) {
        setScaleMPerPx(metersKnown / px);
        setLastScaleMeters(metersKnown);
      }
    }
  }, [scaleKnownM, lastScaleSeg]);

  // When scale changes, recompute meters for stored measurements (if we have mPx)
  useEffect(() => {
    if (!scaleMPerPx) return;
    setMeasures((prev) =>
      prev.map((it) => ({
        ...it,
        m: it.mPx != null ? it.mPx * scaleMPerPx : it.m,
      }))
    );
  }, [scaleMPerPx]);

  // --- State persistence (localStorage) ---
  const STATE_STORAGE_KEY = "kalkulatorStateV2";

  // Load saved state on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STATE_STORAGE_KEY);
      if (!raw) return;
      const s = JSON.parse(raw);
      if (s.bgImgSrc) {
        const img = new Image();
        img.onload = () => {
          setBgImg(img);
        };
        img.src = s.bgImgSrc;
      }
      if (s.view) setView(s.view);
      if (s.scaleMPerPx !== undefined) setScaleMPerPx(s.scaleMPerPx);
      if (s.measures) setMeasures(s.measures);
      if (s.floors) setFloors(s.floors);
      if (s.structuralElements) setStructuralElements(s.structuralElements);
      if (s.lastScaleSeg) setLastScaleSeg(s.lastScaleSeg);
      if (s.lastScaleMeters) setLastScaleMeters(s.lastScaleMeters);
      if (s.scaleKnownM) setScaleKnownM(s.scaleKnownM);
      if (s.heightGlobal) setHeightGlobal(s.heightGlobal);
      if (s.segHeight) setSegHeight(s.segHeight);
      if (s.screedThick) setScreedThick(s.screedThick);
      if (s.screedCoverage) setScreedCoverage(s.screedCoverage);
      if (s.snapEnabled !== undefined) setSnapEnabled(s.snapEnabled);
      if (s.snapStrict !== undefined) setSnapStrict(s.snapStrict);
      if (s.autoPdfScale !== undefined) setAutoPdfScale(s.autoPdfScale);
    } catch (e) {
      console.error("Failed to load saved state:", e);
    }
  }, []);

  // Save state (debounced)
  useEffect(() => {
    const handler = setTimeout(() => {
      try {
        const toSave = {
          bgImgSrc: bgImg ? bgImg.src : null,
          view,
          scaleMPerPx,
          measures,
          floors,
          structuralElements,
          lastScaleSeg,
          lastScaleMeters,
          scaleKnownM,
          heightGlobal,
          segHeight,
          screedThick,
          screedCoverage,
          snapEnabled,
          snapStrict,
          autoPdfScale,
        };
        localStorage.setItem(STATE_STORAGE_KEY, JSON.stringify(toSave));
      } catch (e) {
        console.error("Failed to save state:", e);
      }
    }, 700);
    return () => clearTimeout(handler);
  }, [
    bgImg,
    view,
    scaleMPerPx,
    measures,
    floors,
    lastScaleSeg,
    lastScaleMeters,
    scaleKnownM,
    heightGlobal,
    segHeight,
    screedThick,
    screedCoverage,
    snapEnabled,
    snapStrict,
    autoPdfScale,
  ]);

  // --- POPRAWKA: Lepszy wygląd JSX z Tailwind ---
  const btnClass =
    "px-3 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-100 transition-colors duration-150 flex items-center gap-2 font-medium";
  const btnClassActive =
    "ring-2 ring-orange-500 border-orange-500 bg-orange-50";
  const inputClass =
    "w-full px-2 py-1.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none";
  const cardClass = "bg-white border border-gray-200 shadow-sm rounded-xl p-4";

  const combinedList = useMemo(() => {
    const list = [];
    measures.forEach((m, i) =>
      list.push({
        kind: "measure",
        idx: i,
        mPx: m.mPx,
        m: typeof m.m === "number" ? m.m : null,
        type: m.type,
        h: m.h,
        seg: m.seg,
      })
    );
    floors.forEach((poly, i) =>
      list.push({ kind: "floor", idx: i, poly, area: polyAreaMeters(poly) })
    );
    structuralElements.forEach((el, i) => {
      if (el.kind === "slup") {
        list.push({
          kind: "slup",
          idx: i,
          x: el.x,
          y: el.y,
          w: el.w,
          d: el.d,
          h: el.h,
          area: el.area,
          volume: el.volume,
        });
      } else if (el.kind === "strop") {
        list.push({
          kind: "strop",
          idx: i,
          poly: el.poly,
          area: el.area,
          thickness: el.thickness,
          volume: el.volume,
        });
      } else {
        list.push({
          kind: el.kind,
          idx: i,
          seg: el.seg,
          length: el.length,
          area: el.area,
          volume: el.volume,
        });
      }
    });
    return list;
  }, [measures, floors, structuralElements, polyAreaMeters]);

  function handleExportCsv() {
    const rows = [];
    rows.push(["#", "typ", "długość [m]", "wysokość [m]", "powierzchnia [m²]"]); // header for measures

    const hGlob = Number(heightGlobal) || 0;
    measures.forEach((it, i) => {
      const hUse = typeof it.h === "number" && it.h > 0 ? it.h : hGlob;
      const area =
        it.type !== "grubosc" && hUse > 0 && typeof it.m === "number"
          ? it.m * hUse
          : 0;
      rows.push([
        i + 1,
        it.type,
        typeof it.m === "number" ? it.m.toFixed(3) : "",
        hUse ? hUse.toFixed(3) : "",
        area ? area.toFixed(3) : "",
      ]);
    });

    if (floors.length > 0) {
      rows.push([]);
      rows.push([
        "#",
        "typ",
        "Powierzchnia [m²]",
        "Objętość [m³]",
        "Jastrych 25kg (worki)",
      ]);
      floors.forEach((poly, i) => {
        const area = polyAreaMeters(poly);
        const vol = (area * (Number(screedThick) || 0)) / 100;
        const bags =
          screedCoverage > 0
            ? Math.ceil((area * (Number(screedThick) || 0)) / screedCoverage)
            : 0;
        rows.push([
          i + 1,
          "Posadzka",
          area.toFixed(3),
          vol.toFixed(3),
          String(bags),
        ]);
      });
    }

    // Structural elements (columns, beams, slabs)
    if (structuralElements.length > 0) {
      rows.push([]);
      rows.push([
        "#",
        "typ",
        "objętość [m³]",
        "powierzchnia [m²]",
        "dodatkowe",
      ]);
      structuralElements.forEach((el, i) => {
        const niceName =
          el.kind === "slup"
            ? "Słup"
            : el.kind === "strop"
            ? "Strop"
            : el.kind === "podciag"
            ? "Podciąg"
            : el.kind === "nadciag"
            ? "Nadciąg"
            : el.kind === "wieniec"
            ? "Wieniec"
            : el.kind;
        const areaStr = typeof el.area === "number" ? el.area.toFixed(2) : "";
        if (el.kind === "slup") {
          rows.push([
            i + 1,
            niceName,
            el.volume.toFixed(3),
            areaStr,
            `${el.w}x${el.d} m, h=${el.h} m`,
          ]);
        } else if (el.kind === "strop") {
          rows.push([
            i + 1,
            niceName,
            el.volume.toFixed(3),
            areaStr,
            `grubość=${el.thickness} cm`,
          ]);
        } else {
          rows.push([
            i + 1,
            niceName,
            el.volume.toFixed(3),
            areaStr,
            "segment",
          ]);
        }
      });
    }

    const csv = rows.map((r) => r.join(";")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "pomiary.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <div className="grid md:grid-cols-[380px_1fr] min-h-screen bg-gray-50 text-gray-900">
      <aside
        className={`border-r border-gray-200 p-4 overflow-auto bg-white flex flex-col gap-4`}
      >
        <div>
          <h1 className="text-xl font-semibold">Pomiary z planu</h1>
          <div className="text-sm text-gray-500 my-2">
            Ustaw skalę na odcinku AB – wpisz np. <b>5.17 m</b>, kliknij A→B w
            trybie <b>Ustaw skalę</b>.
          </div>
        </div>

        <div className={cardClass}>
          <label className="text-sm font-medium block mb-2">
            1. Załaduj plan (PNG/JPG/PDF)
          </label>
          <input
            ref={fileRef}
            onChange={handleFileInputChange}
            type="file"
            accept="image/*,.pdf"
            className="w-full text-sm file:mr-4 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100 cursor-pointer"
          />
          {pdfDoc && (
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={() => setPageNum((p) => Math.max(1, p - 1))}
                disabled={pageNum <= 1}
                className="px-2 py-1 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-100 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                &lt; Poprzednia
              </button>
              <span className="text-sm font-medium flex-grow text-center">
                Strona {pageNum} / {numPages}
              </span>
              <button
                onClick={() => setPageNum((p) => Math.min(numPages, p + 1))}
                disabled={pageNum >= numPages}
                className="px-2 py-1 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-100 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Następna &gt;
              </button>
            </div>
          )}
        </div>

        <div className={cardClass}>
          <label className="text-sm font-medium block mb-2">
            2. Wybierz narzędzie
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setMode("scale")}
              className={`${btnClass} ${
                mode === "scale" ? btnClassActive : ""
              }`}
            >
              📏 Ustaw skalę
            </button>
            <input
              value={scaleKnownM}
              onChange={(e) => setScaleKnownM(e.target.value)}
              className="w-24 px-2 py-2 rounded-lg border border-gray-300"
              title="Długość odcinka AB [m]"
            />
            <button
              onClick={() => setMode("measure")}
              className={`${btnClass} ${
                mode === "measure" ? btnClassActive : ""
              }`}
            >
              📐 Odcinek
            </button>
            <button
              onClick={() => setMode("thick")}
              className={`${btnClass} ${
                mode === "thick" ? btnClassActive : ""
              }`}
            >
              🧱 Grubość
            </button>
            <button
              onClick={() => setMode("floor")}
              className={`${btnClass} ${
                mode === "floor" ? btnClassActive : ""
              }`}
            >
              🏠 Posadzka
            </button>
            <button
              onClick={() => setMode("strop")}
              className={`${btnClass} ${
                mode === "strop" ? btnClassActive : ""
              }`}
            >
              🧱 Strop
            </button>
            <button
              onClick={() => setMode("column")}
              className={`${btnClass} ${
                mode === "column" ? btnClassActive : ""
              }`}
            >
              🧱 Słup
            </button>
            <button
              onClick={() => setMode("podciag")}
              className={`${btnClass} ${
                mode === "podciag" ? btnClassActive : ""
              }`}
            >
              🔩 Podciąg
            </button>
            <button
              onClick={() => setMode("nadciag")}
              className={`${btnClass} ${
                mode === "nadciag" ? btnClassActive : ""
              }`}
            >
              🔩 Nadciąg
            </button>
            <button
              onClick={() => setMode("wieniec")}
              className={`${btnClass} ${
                mode === "wieniec" ? btnClassActive : ""
              }`}
            >
              🧭 Wieńce
            </button>
          </div>
          <div className="border-t my-3"></div>
          <div className="flex flex-wrap gap-2">
            <button onClick={handleAnalyzeClick} className={btnClass}>
              🔬 Analizuj
            </button>
            <button onClick={handleUndo} className={btnClass}>
              ↩️ Cofnij
            </button>
            <button
              onClick={handleClear}
              className={`${btnClass} text-red-600 hover:bg-red-50`}
            >
              ❌ Wyczyść
            </button>
          </div>
        </div>

        <div className={cardClass}>
          <h3 className="text-sm font-medium">Ustawienia</h3>
          <label className="text-sm block mt-2">
            <input
              type="checkbox"
              checked={snapEnabled}
              onChange={(e) => setSnapEnabled(e.target.checked)}
              className="mr-2 rounded text-orange-600 focus:ring-orange-500"
            />
            Przyciągaj
          </label>
          <label className="text-sm block mt-2">
            <input
              type="checkbox"
              checked={snapStrict}
              onChange={(e) => setSnapStrict(e.target.checked)}
              className="mr-2 rounded text-orange-600 focus:ring-orange-500"
            />
            Tylko na przecięciach
          </label>
          <label className="text-sm block mt-2">
            <input
              type="checkbox"
              checked={autoPdfScale}
              onChange={(e) => setAutoPdfScale(e.target.checked)}
              className="mr-2 rounded text-orange-600 focus:ring-orange-500"
            />
            Auto-skala z PDF
          </label>
        </div>

        <div className={cardClass}>
          <h3 className="text-sm font-medium">Status</h3>
          <table className="w-full text-sm mt-2">
            <tbody>
              {/* POPRAWKA: Nowy, czytelny feedback skali */}
              <tr className="border-b">
                <th className="text-left font-normal text-gray-600 py-1.5">
                  Skala (1m)
                </th>
                <td className="text-right font-medium text-orange-600">
                  {scaleFeedback.mToPx}
                </td>
              </tr>
              <tr className="border-b">
                <th className="text-left font-normal text-gray-600 py-1.5">
                  Skala (100px)
                </th>
                <td className="text-right font-medium text-orange-600">
                  {scaleFeedback.pxToM}
                </td>
              </tr>
              <tr className="border-b">
                <th className="text-left font-normal text-gray-600 py-1.5">
                  Ostatni pomiar
                </th>
                <td className="text-right font-medium">{lastLenText}</td>
              </tr>
              <tr className="border-b">
                <th className="text-left font-normal text-gray-600 py-1.5">
                  Suma odcinków
                </th>
                <td className="text-right font-medium">{sumLenText}</td>
              </tr>
              <tr>
                <th className="text-left font-normal text-gray-600 py-1.5">
                  Pow. ścian (∑ odc. × h)
                </th>
                <td className="text-right font-medium">{wallAreaText}</td>
              </tr>
            </tbody>
          </table>

          <div className="mt-2">
            <label className="text-xs text-gray-600">
              Wys. ściany [m] (dla kolejnego odcinka)
            </label>
            <input
              value={segHeight}
              onChange={(e) => setSegHeight(e.target.value)}
              type="number"
              step="0.01"
              placeholder="np. 3.00"
              className={inputClass}
            />
          </div>
        </div>
        <div className={cardClass}>
          <h3 className="text-sm font-medium">Posadzka</h3>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div>
              <label className="text-xs text-gray-600">
                Grubość wylewki [cm]
              </label>
              <input
                value={screedThick}
                onChange={(e) => setScreedThick(Number(e.target.value))}
                type="number"
                step="0.5"
                className={inputClass}
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">
                Pokrycie 25kg (m²/cm)
              </label>
              <input
                value={screedCoverage}
                onChange={(e) => setScreedCoverage(Number(e.target.value))}
                type="number"
                step="0.1"
                className={inputClass}
              />
            </div>
          </div>
          <div className="mt-3 flex flex-col gap-2">
            <button onClick={handleCalcFloor} className={btnClass}>
              🏠 Oblicz posadzkę (z obrysu)
            </button>
            <button onClick={handleCalcStrop} className={btnClass}>
              🧱 Oblicz strop (z obrysu)
            </button>
          </div>
          <table className="w-full text-sm mt-2">
            <tbody>
              <tr className="border-b">
                <th className="text-left font-normal text-gray-600 py-1.5">
                  Powierzchnia
                </th>
                <td className="text-right font-medium">{floorAreaText}</td>
              </tr>
              <tr className="border-b">
                <th className="text-left font-normal text-gray-600 py-1.5">
                  Objętość
                </th>
                <td className="text-right font-medium">{floorVolText}</td>
              </tr>
              <tr>
                <th className="text-left font-normal text-gray-600 py-1.5">
                  Jastrych 25kg (worki)
                </th>
                <td className="text-right font-medium">{floorBagsText}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className={cardClass}>
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-medium">Lista pomiarów</h3>
            <button
              onClick={handleExportCsv}
              className={`${btnClass} py-1 px-2 text-sm`}
            >
              📄 Eksport CSV
            </button>
          </div>
          <div className="max-h-60 overflow-y-auto mt-2 text-sm border rounded-lg">
            <table className="w-full">
              <thead className="sticky top-0 bg-gray-50 z-10">
                <tr>
                  <th className="px-2 py-1 text-left text-xs font-semibold text-gray-600">
                    #
                  </th>
                  <th className="px-2 py-1 text-left text-xs font-semibold text-gray-600">
                    Typ
                  </th>
                  <th className="px-2 py-1 text-left text-xs font-semibold text-gray-600">
                    Dł.
                  </th>
                  <th className="px-2 py-1 text-left text-xs font-semibold text-gray-600">
                    Wys.
                  </th>
                  <th className="px-2 py-1 text-left text-xs font-semibold text-gray-600">
                    Pow.
                  </th>
                </tr>
              </thead>
              <tbody>
                {combinedList.length === 0 && (
                  <tr>
                    <td colSpan="5" className="text-center text-gray-500 py-4">
                      Brak pomiarów
                    </td>
                  </tr>
                )}
                {combinedList.map((item, i) => {
                  if (item.kind === "floor") {
                    return (
                      <tr
                        key={`floor-${i}`}
                        className="border-t hover:bg-gray-50"
                      >
                        <td className="px-2 py-1">{i + 1}</td>
                        <td className="px-2 py-1">Posadzka</td>
                        <td className="px-2 py-1">—</td>
                        <td className="px-2 py-1">—</td>
                        <td className="px-2 py-1">{item.area.toFixed(2)} m²</td>
                      </tr>
                    );
                  }

                  if (item.kind === "slup") {
                    return (
                      <tr
                        key={`slup-${i}`}
                        className="border-t hover:bg-gray-50"
                      >
                        <td className="px-2 py-1">{i + 1}</td>
                        <td className="px-2 py-1">Słup</td>
                        <td className="px-2 py-1">—</td>
                        <td className="px-2 py-1">—</td>
                        <td className="px-2 py-1">
                          {item.volume.toFixed(3)} m³
                          {typeof item.area === "number"
                            ? ` / ${item.area.toFixed(2)} m²`
                            : ""}
                        </td>
                      </tr>
                    );
                  }

                  if (item.kind === "strop") {
                    return (
                      <tr
                        key={`strop-${i}`}
                        className="border-t hover:bg-gray-50"
                      >
                        <td className="px-2 py-1">{i + 1}</td>
                        <td className="px-2 py-1">Strop</td>
                        <td className="px-2 py-1">—</td>
                        <td className="px-2 py-1">—</td>
                        <td className="px-2 py-1">
                          {item.volume.toFixed(3)} m³
                          {typeof item.area === "number"
                            ? ` / ${item.area.toFixed(2)} m²`
                            : ""}
                        </td>
                      </tr>
                    );
                  }

                  if (
                    item.kind === "podciag" ||
                    item.kind === "nadciag" ||
                    item.kind === "wieniec"
                  ) {
                    const niceName =
                      item.kind === "podciag"
                        ? "Podciąg"
                        : item.kind === "nadciag"
                        ? "Nadciąg"
                        : "Wieniec";
                    return (
                      <tr
                        key={`${item.kind}-${i}`}
                        className="border-t hover:bg-gray-50"
                      >
                        <td className="px-2 py-1">{i + 1}</td>
                        <td className="px-2 py-1">{niceName}</td>
                        <td className="px-2 py-1">
                          {typeof item.length === "number"
                            ? `${item.length.toFixed(2)} m`
                            : "—"}
                        </td>
                        <td className="px-2 py-1">—</td>
                        <td className="px-2 py-1">
                          {item.volume.toFixed(3)} m³
                          {typeof item.area === "number"
                            ? ` / ${item.area.toFixed(2)} m²`
                            : ""}
                        </td>
                      </tr>
                    );
                  }

                  const hGlob = Number(heightGlobal) || 0;
                  const hUse =
                    typeof item.h === "number" && item.h > 0 ? item.h : hGlob;
                  const area =
                    item.type !== "grubosc" &&
                    hUse > 0 &&
                    typeof item.m === "number"
                      ? item.m * hUse
                      : 0;
                  return (
                    <tr
                      key={`measure-${i}`}
                      className="border-t hover:bg-gray-50"
                    >
                      <td className="px-2 py-1">{i + 1}</td>
                      <td className="px-2 py-1">
                        {item.type === "grubosc" ? "Grubość" : "Odcinek"}
                      </td>
                      <td className="px-2 py-1">
                        {typeof item.m === "number"
                          ? `${item.m.toFixed(2)} m`
                          : item.mPx != null
                          ? `${item.mPx.toFixed(0)} px`
                          : "—"}
                      </td>
                      <td className="px-2 py-1">
                        {hUse ? hUse.toFixed(2) : "—"} m
                      </td>
                      <td className="px-2 py-1">
                        {area > 0 ? area.toFixed(2) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className={cardClass}>
          <h3 className="text-sm font-medium">Podsumowanie wyników</h3>
          <table className="w-full text-sm mt-2">
            <tbody>
              <tr className="border-b">
                <th className="text-left font-normal text-gray-600 py-1.5">
                  Suma długości
                </th>
                <td className="text-right font-medium">{sumLenText}</td>
              </tr>
              <tr className="border-b">
                <th className="text-left font-normal text-gray-600 py-1.5">
                  Długość konstrukcji (podciągi/nadciągi/wieńce)
                </th>
                <td className="text-right font-medium">
                  {structuralTotalLenText}
                </td>
              </tr>
              <tr className="border-b">
                <th className="text-left font-normal text-gray-600 py-1.5">
                  Suma wysokości
                </th>
                <td className="text-right font-medium">{sumHeightText}</td>
              </tr>
              <tr className="border-b">
                <th className="text-left font-normal text-gray-600 py-1.5">
                  Powierzchnia ścian
                </th>
                <td className="text-right font-medium">{wallAreaText}</td>
              </tr>
              <tr className="border-b">
                <th className="text-left font-normal text-gray-600 py-1.5">
                  Powierzchnia posadzek
                </th>
                <td className="text-right font-medium">{floorAreaText}</td>
              </tr>
              <tr className="border-b">
                <th className="text-left font-normal text-gray-600 py-1.5">
                  Objętość wylewki
                </th>
                <td className="text-right font-medium">{floorVolText}</td>
              </tr>
              <tr>
                <th className="text-left font-normal text-gray-600 py-1.5">
                  Worki jastrychu (25kg)
                </th>
                <td className="text-right font-medium">{floorBagsText}</td>
              </tr>
              <tr className="border-b">
                <th className="text-left font-normal text-gray-600 py-1.5">
                  Objętość konstrukcji (słupy/podciągi/stropy)
                </th>
                <td className="text-right font-medium">
                  {structuralTotalVolumeText}
                </td>
              </tr>
              <tr>
                <th className="text-left font-normal text-gray-600 py-1.5">
                  Powierzchnia konstrukcji (słupy/podciągi/stropy)
                </th>
                <td className="text-right font-medium">
                  {structuralTotalAreaText}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="text-xs text-gray-500 mt-auto pt-2">
          LPM – punkty • PPM – przesuwanie • rolka – zoom • ESC – anuluj.
        </div>
      </aside>

      <main className="relative">
        <div className="stage w-full h-screen">
          <canvas
            ref={canvasRef}
            className="w-full h-full bg-slate-100 cursor-crosshair"
          />
          <div className="absolute left-3 top-3 bg-white/90 border border-gray-200 shadow-lg rounded-lg px-3 py-2 text-sm font-medium">
            {hint}
          </div>
        </div>
      </main>
    </div>
  );
}
